import axios from "axios";
import { API } from "@/utils/constants";

const INV = `${API}/inventory`;

// Automatically attach the logged-in user's id to every inventory request so
// the backend can enforce parts-permission tiers. Consistent with the app's
// existing identity model (user stored in localStorage on login).
axios.interceptors.request.use((config) => {
  if (config.url && config.url.includes("/inventory")) {
    try {
      const u = JSON.parse(localStorage.getItem("fleetUser") || "null");
      if (u?.id) config.params = { ...(config.params || {}), actor_id: u.id };
    } catch (e) {
      // ignore malformed storage
    }
  }
  return config;
});

export const inventoryApi = {
  // Dashboard
  getDashboard: () => axios.get(`${INV}/dashboard`).then((r) => r.data),

  // Categories
  getCategories: () => axios.get(`${INV}/categories`).then((r) => r.data),
  createCategory: (data) => axios.post(`${INV}/categories`, data).then((r) => r.data),
  addSubcategory: (id, subcategory) =>
    axios.post(`${INV}/categories/${id}/subcategories`, { subcategory }).then((r) => r.data),

  // Vendors
  getVendors: (includeArchived = false) =>
    axios.get(`${INV}/vendors?include_archived=${includeArchived}`).then((r) => r.data),
  createVendor: (data) => axios.post(`${INV}/vendors`, data).then((r) => r.data),
  updateVendor: (id, data) => axios.put(`${INV}/vendors/${id}`, data).then((r) => r.data),

  // Locations
  getLocations: (includeArchived = false) =>
    axios.get(`${INV}/locations?include_archived=${includeArchived}`).then((r) => r.data),
  createLocation: (data) => axios.post(`${INV}/locations`, data).then((r) => r.data),
  updateLocation: (id, data) => axios.put(`${INV}/locations/${id}`, data).then((r) => r.data),

  // Parts
  getParts: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return axios.get(`${INV}/parts?${qs}`).then((r) => r.data);
  },
  getPart: (id) => axios.get(`${INV}/parts/${id}`).then((r) => r.data),
  checkDuplicate: (data) => axios.post(`${INV}/parts/check-duplicate`, data).then((r) => r.data),
  createPart: (data) => axios.post(`${INV}/parts`, data).then((r) => r.data),
  bulkImportParts: (data) => axios.post(`${INV}/parts/bulk-import`, data).then((r) => r.data),
  updatePart: (id, data) => axios.put(`${INV}/parts/${id}`, data).then((r) => r.data),
  deletePart: (id) => axios.delete(`${INV}/parts/${id}`).then((r) => r.data),
  adjustPart: (id, data) => axios.post(`${INV}/parts/${id}/adjust`, data).then((r) => r.data),
  mergeParts: (targetId, data) => axios.post(`${INV}/parts/${targetId}/merge`, data).then((r) => r.data),
  transferPartLocation: (id, data) => axios.post(`${INV}/parts/${id}/transfer-location`, data).then((r) => r.data),

  // Transactions
  getTransactions: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return axios.get(`${INV}/transactions?${qs}`).then((r) => r.data);
  },
  createTransaction: (data) => axios.post(`${INV}/transactions`, data).then((r) => r.data),

  // Audit
  getAudit: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return axios.get(`${INV}/audit?${qs}`).then((r) => r.data);
  },
  getAuditMeta: () => axios.get(`${INV}/audit/meta`).then((r) => r.data),

  // ---- Procurement (Phase 2) ----
  // Parts Requests
  getRequests: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return axios.get(`${INV}/requests?${qs}`).then((r) => r.data);
  },
  getRequest: (id) => axios.get(`${INV}/requests/${id}`).then((r) => r.data),
  createRequest: (data) => axios.post(`${INV}/requests`, data).then((r) => r.data),
  updateRequestStatus: (id, data) => axios.put(`${INV}/requests/${id}/status`, data).then((r) => r.data),
  createPoFromRequest: (id, data) => axios.post(`${INV}/requests/${id}/create-po`, data).then((r) => r.data),

  // Purchase Orders
  getPurchaseOrders: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return axios.get(`${INV}/purchase-orders?${qs}`).then((r) => r.data);
  },
  getPurchaseOrder: (id) => axios.get(`${INV}/purchase-orders/${id}`).then((r) => r.data),
  createPurchaseOrder: (data) => axios.post(`${INV}/purchase-orders`, data).then((r) => r.data),
  updatePurchaseOrder: (id, data) => axios.put(`${INV}/purchase-orders/${id}`, data).then((r) => r.data),

  // Receiving
  receiveParts: (data) => axios.post(`${INV}/receive`, data).then((r) => r.data),
  getReceipts: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return axios.get(`${INV}/receipts?${qs}`).then((r) => r.data);
  },

  // Use Parts / Unit History
  useParts: (data) => axios.post(`${INV}/use-parts`, data).then((r) => r.data),
  getUnitHistory: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return axios.get(`${INV}/unit-history?${qs}`).then((r) => r.data);
  },
  getUnitCostSummary: (unitId) => axios.get(`${INV}/units/${unitId}/cost-summary`).then((r) => r.data),

  // Core Tracking (Phase 3)
  getCores: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return axios.get(`${INV}/cores?${qs}`).then((r) => r.data);
  },
  getCoresSummary: () => axios.get(`${INV}/cores/summary`).then((r) => r.data),
  getCore: (id) => axios.get(`${INV}/cores/${id}`).then((r) => r.data),
  createCore: (data) => axios.post(`${INV}/cores`, data).then((r) => r.data),
  updateCore: (id, data) => axios.put(`${INV}/cores/${id}`, data).then((r) => r.data),

  // Specialized inventory (Phase 4)
  getSalvagedParts: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return axios.get(`${INV}/salvaged-parts?${qs}`).then((r) => r.data);
  },
  createSalvagedPart: (data) => axios.post(`${INV}/salvaged-parts`, data).then((r) => r.data),
  updateSalvagedPart: (id, data) => axios.put(`${INV}/salvaged-parts/${id}`, data).then((r) => r.data),
  deleteSalvagedPart: (id) => axios.delete(`${INV}/salvaged-parts/${id}`).then((r) => r.data),
  getTires: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return axios.get(`${INV}/tires?${qs}`).then((r) => r.data);
  },
  createTire: (data) => axios.post(`${INV}/tires`, data).then((r) => r.data),
  updateTire: (id, data) => axios.put(`${INV}/tires/${id}`, data).then((r) => r.data),
  deleteTire: (id) => axios.delete(`${INV}/tires/${id}`).then((r) => r.data),
  installTire: (id, data) => axios.post(`${INV}/tires/${id}/install`, data).then((r) => r.data),
  createAdjustment: (data) => axios.post(`${INV}/adjustments`, data).then((r) => r.data),
  getAdjustments: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return axios.get(`${INV}/adjustments?${qs}`).then((r) => r.data);
  },
  createCycleCount: (data) => axios.post(`${INV}/cycle-counts`, data).then((r) => r.data),
  getCycleCounts: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return axios.get(`${INV}/cycle-counts?${qs}`).then((r) => r.data);
  },
  getSpecializedValuation: () => axios.get(`${INV}/specialized-valuation`).then((r) => r.data),

  // Reports (Phase 5)
  getInventoryReport: () => axios.get(`${INV}/reports/inventory`).then((r) => r.data),
  getPurchasingReport: () => axios.get(`${INV}/reports/purchasing`).then((r) => r.data),
  getUnitsReport: () => axios.get(`${INV}/reports/units`).then((r) => r.data),
  getCoresReport: () => axios.get(`${INV}/reports/cores`).then((r) => r.data),
  getRequestsReport: () => axios.get(`${INV}/reports/requests`).then((r) => r.data),
  getTiresReport: () => axios.get(`${INV}/reports/tires`).then((r) => r.data),
  getSalvageReport: () => axios.get(`${INV}/reports/salvage`).then((r) => r.data),
};
